import { Chain, Connector, ConnectorData } from 'wagmi';
import { TronWeb, trx } from '../types/tronWeb';
import { TRON_CHAIN_ID } from '../helpers/customChains';
import { WalletsName, WalletsType } from '../components/ConnectedWallet/wallets.ls';

// Try to wait for tonlink to be injected for 2 seconds
// if not, cancel interval and trhow error
const waitForTronLink = async () => {
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(() => {
      if (window.tronLink) {
        clearInterval(checkInterval);
        resolve(1);
      }
    }, 10);

    setTimeout(() => {
      clearInterval(checkInterval);
      reject(new Error('Timeout: TronLink not detected after 2 seconds'));
    }, 2000);
  });
};

interface requestAccountsResponse {
  code: number; // 200：ok 4000：in queue, no need to repeat commit， 4001：user rejected
  message: string;
}

export class TronConnector extends Connector<any> {
  readonly id = WalletsType.TronLink;
  readonly name = WalletsName.TronLink;
  readonly ready = true;

  provider?: TronWeb;
  signer: trx | undefined;

  constructor(chains: Chain[]) {
    super({ chains, options: {} });

    this.onDisconnect = this.onDisconnect.bind(this);
  }

  async getProvider() {
    await waitForTronLink();
    console.log('TronLink detected');

    // If tronLink not connected, request accounts, otherwise start using tonWeb
    if (!window.tronLink.ready) {
      const res: requestAccountsResponse = await window.tronLink.request({
        method: 'tron_requestAccounts',
      });

      if (
        window.tronWeb &&
        window.tronWeb.defaultAddress.base58 &&
        res.code === 200
      ) {
        this.provider = window.tronWeb;
      } else {
        throw new Error(res.message);
      }
    } else {
      this.provider = window.tronWeb;
    }

    return this.provider;
  }

  async connect(
    config?: { chainId?: number | undefined } | undefined
  ): Promise<Required<ConnectorData>> {
    this.emit('message', { type: 'connecting' });

    try {
      await this.getProvider();
    } catch (e: any) {
      throw new Error(e.message);
    }
    window.addEventListener('message', this.onDisconnect);

    const { base58 } = this.provider?.defaultAddress || {};
    return {
      account: base58,
      chain: {
        id: TRON_CHAIN_ID,
        unsupported: false,
      },
      provider: this.provider,
    } as Required<ConnectorData>;
  }

  // No possible ways to programmatically disconnect tronLink wallet
  async disconnect(): Promise<void> {
    console.log('Tron wallet disconnected');
    window.removeEventListener('message', this.onDisconnect);
  }

  async getAccount(): Promise<`0x${string}`> {
    const { base58 } = this.provider?.defaultAddress || {};
    return base58 as any;
  }

  async getChainId(): Promise<number> {
    return TRON_CHAIN_ID;
  }

  async getWalletClient(
    config?: { chainId?: number | undefined } | undefined
  ): Promise<any> {
    return {
      async signTypedData({ message }: any) {
        const msg = JSON.stringify(message);

        // This one not working as we have defaultPrivateKey = false
        // const signature = await (this.provider as any)?.trx._signTypedData(
        //   domain,
        //   types,
        //   value
        // );

        try {
          const signature = await window.tronWeb?.trx.signMessageV2(msg);

          return signature;
        } catch (e: any) {
          throw new Error(e);
        }
      },
    };
  }

  async isAuthorized(): Promise<boolean> {
    const { fullNode, solidityNode, eventServer } =
      (await this.provider?.isConnected()) || {};

    return fullNode && solidityNode && eventServer;
  }

  protected onAccountsChanged(accounts: `0x${string}`[]): void {
    throw new Error('Method onAccountsChanged not implemented.');
  }

  protected onChainChanged(chain: string | number): void {
    throw new Error('Method onChainChanged not implemented.');
  }

  protected onDisconnect(e: any) {
    if (e.data.message && e.data.message.action == 'setAccount') {
      if (!e.data.message.data.address) {
        this.emit('disconnect');
      }
    }
  }
}
